import { FieldPolicy, Reference } from "@apollo/client";

type LimitOffsetResponse = {
  edges: Reference[];
};

type LimitOffsetFieldPolicy<T = unknown> = FieldPolicy<T & LimitOffsetResponse>;

/**
 * A field policy that handles pagination with limit and offset for fields with an `edges` array.
 *
 * @param keyArgs If a paginated field accepts arguments in addition to offset and limit which are
 *        used for filtering, you can set that here. An array of keys should be passed that
 *        determines which list to store the results in.
 *
 * NOTE: We can't use @apollo/client's built-in "offsetLimitPagination" because it follows a
 * different pagination pattern than our API and doesn't offer a way to customize it.
 */
export function limitOffsetPagination<T>(
  keyArgs: FieldPolicy<T>["keyArgs"] = false
): LimitOffsetFieldPolicy<T> {
  return {
    keyArgs,

    merge(existing, incoming, { args }) {
      if (!args) {
        throw new Error(
          "limitOffsetPagination: expected args for offset-based pagination"
        );
      }

      const createReturn = (edges: Reference[]) => ({
        ...incoming,
        edges,
      });

      const nextEdges = existing?.edges?.slice(0) ?? [];

      if (!incoming.edges) {
        return createReturn(nextEdges);
      }

      const { offset = 0 } = args;

      // Add new items to the correct place based on offset
      incoming.edges.forEach((edge, i) => {
        nextEdges[offset + i] = edge;
      });

      return createReturn(nextEdges);
    },
  };
}

export function filterOnAllKeys(
  ignoredKeys: string[] = []
): FieldPolicy["keyArgs"] {
  const allIgnoredKeys = ["limit", "offset", ...ignoredKeys];

  return (args) => {
    if (!args) {
      return [];
    }

    return Object.keys(args).filter((key) => !allIgnoredKeys.includes(key));
  };
}
